<template>
  <div>
    <div class="payment-receipt-view">
      <div class="actions mb-3 d-flex justify-content-end">
        <b-button
          size="lg"
          variant="success"
          class="print-btn"
          :disabled="isGenerating"
          @click="generatePdf"
        >
          <feather-icon
            v-if="!isGenerating"
            icon="PrinterIcon"
            size="18"
            class="mr-50"
          />
          <b-spinner
            v-if="isGenerating"
            small
            class="mr-50"
          />
          <span v-if="isGenerating">{{
            $t("common.loading") || "Loading..."
          }}</span>
          <span v-else>{{ $t("common.print") || "Print" }}</span>
        </b-button>
      </div>

      <div
        id="printableContent"
        ref="printContent"
        class="receipt-content"
        dir="rtl"
      >
        <div class="header">
          <div class="brand">
            <div class="brand-title">
              {{ getReceiptTitle() }}
            </div>
            <div class="brand-sub">
              وصل دفع للطبيب
            </div>
            <div class="brand-info">
              {{ doctorName }}
            </div>
          </div>
          <div class="date-info">
            <div>تاريخ الطباعة: {{ printedAt }}</div>
            <div v-if="fromDate || toDate">
              الفترة:
              <span v-if="fromDate">من {{ fromDate }}</span>
              <span v-if="toDate">إلى {{ toDate }}</span>
            </div>
          </div>
        </div>

        <div class="meta-grid">
          <div class="meta-item">
            <span class="label">{{ $t("common.payment-type") }}:</span>
            <!-- <span class="value">{{
              isAllPayment ? $t("surgery.pay-all") : $t("surgery.pay-one")
            }}</span> -->
          </div>
          <div class="meta-item">
            <span class="label">{{ $t("common.total-items") }}:</span>
            <span class="value">{{ data.length }}</span>
          </div>
          <!-- doctor total below table -->
        </div>

        <table class="receipt-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>{{ $t("reception.patient-name") }}</th>
              <th>الخدمة / العملية</th>
              <th v-if="isDoctorAssistant">
                {{ $t("accounting.doctor_assistant_price") }}
              </th>
              <template v-else>
                <th>{{ $t("reception.Surgery Price") }}</th>
                <th>{{ $t("surgery.doctor_price") }}</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in data"
              :key="index"
            >
              <td>{{ getDate(item) }}</td>
              <td>{{ getPatientName(item) }}</td>
              <td>{{ getSurgeryName(item) }}</td>
              <td
                v-if="isDoctorAssistant"
                class="amount-cell"
              >
                {{ formatAmount(getDoctorAssistantPrice(item)) }}
              </td>
              <template v-else>
                <td class="amount-cell">
                  {{ formatAmount(getTotalPrice(item)) }}
                </td>
                <td class="amount-cell">
                  {{ formatAmount(getDoctorPrice(item)) }}
                </td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr v-if="isDoctorAssistant">
              <td
                colspan="3"
                class="text-right font-weight-bold"
              >
                مجموع حصة المساعد
              </td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(totalDoctorAssistantPrice) }}
              </td>
            </tr>
            <tr v-else>
              <td
                colspan="3"
                class="text-right font-weight-bold"
              >
                {{ $t("hospital.doctorTotalPrices") }}
              </td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(totalSurgeryPrice) }}
              </td>
              <td class="font-weight-bold amount-cell">
                {{ formatAmount(totalDoctorPrice) }}
              </td>
            </tr>
          </tfoot>
        </table>

        <!-- Daily summary -->
        <div
          v-if="Object.keys(dailyDoctorTotals).length"
          class="daily-summary"
        >
          <table class="summary-table">
            <thead>
              <tr>
                <th>التاريخ</th>
                <th v-if="isDoctorAssistant">
                  حصة المساعد
                </th>
                <th v-else>
                  حصة الطبيب
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(amount, day) in dailyDoctorTotals"
                :key="day"
              >
                <td>{{ day }}</td>
                <td class="amount-cell">
                  {{ formatAmount(amount) }}
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold text-right">
                  المجموع الكلي
                </td>
                <td class="amount-cell font-weight-bold">
                  {{ formatAmount(isDoctorAssistant ? totalDoctorAssistantPrice : totalDoctorPrice) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="footer-note">
          <div class="signature">
            <div class="sig-line" />
            <div class="sig-label">
              {{ $t("common.signature") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DoctorPaymentReceipt',
  props: {
    data: {
      type: Array,
      required: true,
      default: () => [],
    },
    doctorName: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      required: true,
      validator: value => [
        'surgeries',
        'consultancies',
        'patient-stay',
        'nicu',
        'unified',
      ].includes(value),
    },
    isAllPayment: {
      type: Boolean,
      default: false,
    },
    isDoctorAssistant: {
      type: Boolean,
      default: false,
    },
    totalAmount: {
      type: Number,
      default: 0,
    },
    totalDiscount: {
      type: Number,
      default: 0,
    },
    fromDate: {
      type: String,
      default: '',
    },
    toDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isGenerating: false,
      printedAt: new Date().toLocaleString(),
    }
  },
  computed: {
    netTotal() {
      // Use doctor assistant price total for assistants, doctor price for doctors
      return this.isDoctorAssistant ? this.totalDoctorAssistantPrice : this.totalDoctorPrice
    },
    totalDoctorAssistantPrice() {
      // Sum of all doctor_assistant_price values
      return this.data.reduce(
        (sum, item) => sum + parseFloat(this.getDoctorAssistantPrice(item) || 0),
        0,
      )
    },
    totalDoctorPrice() {
      // Sum of all doctor_price values
      return this.data.reduce(
        (sum, item) => sum + parseFloat(this.getDoctorPrice(item) || 0),
        0,
      )
    },
    totalSurgeryPrice() {
      // Sum of all total_price values
      return this.data.reduce(
        (sum, item) => sum + parseFloat(this.getTotalPrice(item) || 0),
        0,
      )
    },
    dailyDoctorTotals() {
      const totals = {};
      (this.data || []).forEach(item => {
        const day = (this.getDate(item) || '').split(' ')[0]
        if (!day) return
        const amount = this.isDoctorAssistant
          ? parseFloat(this.getDoctorAssistantPrice(item) || 0)
          : parseFloat(this.getDoctorPrice(item) || 0)
        totals[day] = (totals[day] || 0) + amount
      })
      return totals
    },
  },
  methods: {
    getReceiptTitle() {
      const titles = {
        surgeries: this.$t('hospital.doctor-surgeries'),
        consultancies: this.$t('surgery.doctor-consultancies'),
        'patient-stay': this.$t('hospital.doctor-patient-stay'),
        nicu: this.$t('incubator.nicu-patients'),
      }
      const typeTitle = titles[this.type] || this.$t('surgery.payment-receipt')
      const paymentType = this.isAllPayment
        ? this.$t('surgery.pay-all')
        : this.$t('surgery.pay-one')
      return `${typeTitle} -  `
    },
    formatAmount(amount) {
      if (amount == null || amount === '') return '-'
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'IQD',
          maximumFractionDigits: 0,
        }).format(Number(amount))
      } catch (e) {
        return amount
      }
    },
    getPatientName(item) {
      // Handle unified data structure
      if (this.type === 'unified' || item.patient_name) {
        return item.patient_name || '-'
      }
      if (this.type === 'nicu') {
        return (
          (item.patient_card
            && item.patient_card.patient
            && item.patient_card.patient.name)
          || '-'
        )
      }
      if (this.type === 'patient-stay') {
        return (
          (item.patient_card_stay
            && item.patient_card_stay.patient_card
            && item.patient_card_stay.patient_card.patient
            && item.patient_card_stay.patient_card.patient.name)
          || '-'
        )
      }
      return (
        (item.patient_card_surgery
          && item.patient_card_surgery.patient_card
          && item.patient_card_surgery.patient_card.patient
          && item.patient_card_surgery.patient_card.patient.name)
        || '-'
      )
    },
    getPatientPhone(item) {
      if (this.type === 'nicu') {
        return (
          (item.patient_card
            && item.patient_card.patient
            && item.patient_card.patient.phone)
          || '-'
        )
      }
      if (this.type === 'patient-stay') {
        return (
          (item.patient_card_stay
            && item.patient_card_stay.patient_card
            && item.patient_card_stay.patient_card.patient
            && item.patient_card_stay.patient_card.patient.phone)
          || '-'
        )
      }
      return (
        (item.patient_card_surgery
          && item.patient_card_surgery.patient_card
          && item.patient_card_surgery.patient_card.patient
          && item.patient_card_surgery.patient_card.patient.phone)
        || '-'
      )
    },
    getSurgeryName(item) {
      // Handle unified data structure
      if (this.type === 'unified') {
        // Try multiple sources for surgery/service name in priority order

        // 1. Check invoice.surgery_name
        if (item.invoice && item.invoice.surgery_name) {
          return item.invoice.surgery_name
        }
        // 2. Check invoice.service_name
        if (item.invoice && item.invoice.service_name) {
          return item.invoice.service_name
        }
        // 3. Check invoice.name
        if (item.invoice && item.invoice.name) {
          return item.invoice.name
        }

        // 4. Check invoice_out_clinic.surgery_name
        if (item.invoice_out_clinic && item.invoice_out_clinic.surgery_name) {
          return item.invoice_out_clinic.surgery_name
        }
        // 5. Check invoice_out_clinic.service_name
        if (item.invoice_out_clinic && item.invoice_out_clinic.service_name) {
          return item.invoice_out_clinic.service_name
        }
        // 6. Check invoice_out_clinic.name
        if (item.invoice_out_clinic && item.invoice_out_clinic.name) {
          return item.invoice_out_clinic.name
        }
        // 7. Check invoice_out_clinic.service.title
        if (
          item.invoice_out_clinic
          && item.invoice_out_clinic.service
          && item.invoice_out_clinic.service.title
        ) {
          return item.invoice_out_clinic.service.title
        }

        // 8. Check invoice_patient_surgery.surgery_name
        if (
          item.invoice_patient_surgery
          && item.invoice_patient_surgery.surgery_name
        ) {
          return item.invoice_patient_surgery.surgery_name
        }
        // 9. Check invoice_patient_surgery.service_name
        if (
          item.invoice_patient_surgery
          && item.invoice_patient_surgery.service_name
        ) {
          return item.invoice_patient_surgery.service_name
        }
        // 10. Check invoice_patient_surgery.name
        if (item.invoice_patient_surgery && item.invoice_patient_surgery.name) {
          return item.invoice_patient_surgery.name
        }

        // 11. Check root level service_name
        if (item.service_name) {
          return item.service_name
        }
        // 12. Check procedures field
        if (item.procedures) {
          return item.procedures
        }

        // 13. Check nested invoice.service object
        if (
          item.invoice
          && item.invoice.service
          && item.invoice.service.title
        ) {
          return item.invoice.service.title
        }
        // 14. Check nested invoice.surgery object
        if (
          item.invoice
          && item.invoice.surgery
          && item.invoice.surgery.title
        ) {
          return item.invoice.surgery.title
        }

        // 15. Fallback based on unified record type when no explicit name exists
        switch (item.type) {
          case 'stay':
            return 'إقامة المريض'
          case 'outclinic':
            return 'خدمة عيادة خارجية'
          case 'surgery':
            return 'عملية جراحية'
          case 'consultancy':
            return 'استشارة طبية'
          case 'nicu':
            return 'خدمات الخدج / العناية المركزة'
          default:
            return '-'
        }
      }
      if (this.type === 'consultancies') {
        return (
          (item.patient_card_surgery
            && item.patient_card_surgery.surgery
            && item.patient_card_surgery.surgery.title)
          || '-'
        )
      }
      return (
        (item.surgery && item.surgery.title)
        || (item.patient_card_surgery
          && item.patient_card_surgery.surgery
          && item.patient_card_surgery.surgery.title)
        || '-'
      )
    },
    getTotalPrice(item) {
      // Handle unified data structure - use total_price
      if (this.type === 'unified') {
        return parseFloat(item.total_price || 0)
      }
      if (this.type === 'nicu') {
        // Use total_doctor_amount from backend if available
        if (
          item.total_doctor_amount !== undefined
          && item.total_doctor_amount !== null
        ) {
          return parseFloat(item.total_doctor_amount || 0)
        }
        // Fallback: calculate from invoice_patient_incubators array if total_doctor_amount not available
        if (
          item.invoice_patient_incubators
          && Array.isArray(item.invoice_patient_incubators)
          && item.invoice_patient_incubators.length > 0
        ) {
          const totalDoctorAmount = item.invoice_patient_incubators.reduce(
            (sum, invoice) => sum + parseFloat(invoice.doctor_amount || 0),
            0,
          )
          return totalDoctorAmount
        }
        // Final fallback to amount
        return parseFloat(item.amount || 0)
      }
      if (this.type === 'patient-stay') {
        return (
          (item.patient_card_stay && item.patient_card_stay.price)
          || item.amount
          || 0
        )
      }
      if (this.type === 'consultancies') {
        return (
          (item.patient_card_surgery && item.patient_card_surgery.price)
          || item.amount
          || 0
        )
      }
      // For surgeries, show the surgery price
      return (
        (item.patient_card_surgery && item.patient_card_surgery.price)
        || item.amount
        || 0
      )
    },
    getDoctorPrice(item) {
      // Handle unified data structure - use doctor_price
      if (this.type === 'unified') {
        return parseFloat(item.doctor_price || 0)
      }
      if (this.type === 'nicu') {
        return parseFloat(item.doctor_price || item.amount || 0)
      }
      if (this.type === 'patient-stay') {
        return parseFloat(item.doctor_price || 0)
      }
      if (this.type === 'consultancies') {
        return parseFloat(item.doctor_price || 0)
      }
      // For surgeries
      return parseFloat(item.doctor_price || 0)
    },
    getDoctorAssistantPrice(item) {
      // Handle unified data structure - use doctor_assistant_price
      if (this.type === 'unified') {
        return parseFloat(item.doctor_assistant_price || 0)
      }
      // For other types, fallback to doctor_price or amount
      return parseFloat(item.doctor_assistant_price || item.doctor_price || item.amount || 0)
    },
    getAmount(item) {
      // Keep for backward compatibility - returns total price
      return this.getTotalPrice(item)
    },
    getDiscount(item) {
      return item.doctor_discount || 0
    },
    getNetAmount(item) {
      // Handle unified data structure - doctor_price is already the net amount
      if (this.type === 'unified') {
        return parseFloat(item.doctor_price || 0)
      }
      // For surgeries, net amount is doctor_price after discount
      if (this.type === 'surgeries') {
        const doctorPrice = item.doctor_price || 0
        const discount = this.getDiscount(item)
        return doctorPrice - discount
      }
      // For other types, just amount minus discount
      return this.getAmount(item) - this.getDiscount(item)
    },
    getDate(item) {
      // Handle unified data structure - use start_time
      if (this.type === 'unified') {
        return item.approved_at || '-'
      }
      if (this.type === 'nicu') {
        return item.approved_at || '-'
      }
      if (this.type === 'patient-stay') {
        return (
          (item.patient_card_stay && item.patient_card_stay.stay_date_time)
          || item.approved_at
          || '-'
        )
      }
      return (
        (item.patient_card_surgery
          && item.patient_card_surgery.surgery_date_time)
        || item.created_at
        || '-'
      )
    },
    generatePdf() {
      this.isGenerating = true
      const content = this.$refs.printContent
      if (!content) {
        this.isGenerating = false
        return
      }

      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        alert(
          this.$t('common.popup-blocked') || 'Please allow popups for this site',
        )
        this.isGenerating = false
        return
      }

      const htmlContent = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${this.$t('surgery.payment-receipt')}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      padding: 12px;
      color: #111827;
      line-height: 1.4;
      font-size: 11px;
      direction: rtl;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 8px;
    }
    .brand-title {
      font-size: 16px;
      font-weight: 900;
      color: #111827;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .brand-sub {
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
    }
    .brand-info {
      font-size: 20px;
      font-weight: 700;
      color: #111827;
      margin-top: 8px;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 8px;
      margin-bottom: 10px;
      padding: 8px;
      background: #f9fafb;
      border-radius: 8px;
    }
    .meta-item {
      display: flex;
      flex-direction: column;
    }
    .meta-item .label {
      font-size: 12px;
      color: #6b7280;
      font-weight: 600;
      text-transform: uppercase;
    }
    .meta-item .value {
      font-size: 13px;
      color: #111827;
      font-weight: 700;
      margin-top: 4px;
    }
    .receipt-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
    }
    .receipt-table th,
    .receipt-table td {
      padding: 6px 8px;
      text-align: right;
      border: 1px solid #e5e7eb;
    }
    .receipt-table th {
      background: #f3f4f6;
      font-weight: 700;
      color: #111827;
      font-size: 11px;
    }
    .receipt-table tfoot td {
      background: #f9fafb;
      font-weight: 700;
    }
    .daily-summary {
      display: flex;
      justify-content: center;
      margin-top: 8px;
    }
    .summary-table {
      width: 45%;
      border-collapse: collapse;
      font-size: 11px;
    }
    .summary-table th,
    .summary-table td {
      border: 1px solid #e5e7eb;
      padding: 4px 6px;
      text-align: center;
    }
    .summary-table th {
      background: #f3f4f6;
      font-weight: 700;
    }
    .footer-note {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
    .signature {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .sig-line {
      width: 140px;
      height: 1px;
      background: #9ca3af;
      margin-bottom: 4px;
    }
    .sig-label {
      font-size: 12px;
      color: #4b5563;
      font-weight: 600;
    }
    @media print {
      body {
        padding: 10mm;
      }
      .receipt-table {
        page-break-inside: auto;
      }
      .receipt-table tr {
        page-break-inside: avoid;
      }
      .receipt-table thead {
        display: table-header-group;
      }
    }
  </style>
</head>
<body>
  ${content.innerHTML}
  <script>
    window.onload = function() {
      window.print();
    };
  <\/script>
</body>
</html>
      `

      printWindow.document.write(htmlContent)
      printWindow.document.close()
      this.$emit('print-started')
      setTimeout(() => {
        this.isGenerating = false
      }, 1000)
    },
  },
}
</script>

<style scoped>
.payment-receipt-view {
  background: #fff;
  padding: 12px;
  color: #111827;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.4;
  font-size: 12px;
  direction: rtl;
}

.actions {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  padding: 15px 0;
  margin: -20px -20px 20px -20px;
  padding-left: 20px;
  padding-right: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid #10b981;
}

.print-btn {
  font-weight: 700;
  font-size: 16px;
  padding: 12px 24px;
  box-shadow: 0 4px 6px rgba(16, 185, 129, 0.3);
  transition: all 0.3s ease;
}

.print-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(16, 185, 129, 0.4);
}

.print-btn:active:not(:disabled) {
  transform: translateY(0);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
}

.brand-title {
  font-size: 18px;
  font-weight: 900;
  color: #111827;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.brand-sub {
  font-size: 14px;
  font-weight: 800;
  color: #1f2937;
}

.brand-info {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-top: 8px;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
}

.meta-item {
  display: flex;
  flex-direction: column;
}

.meta-item .label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
}

.meta-item .value {
  font-size: 14px;
  color: #111827;
  font-weight: 700;
  margin-top: 4px;
}

.receipt-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.receipt-table th,
.receipt-table td {
  padding: 6px 8px;
  text-align: right;
  border: 1px solid #e5e7eb;
}

.receipt-table th {
  background: #f3f4f6;
  font-weight: 700;
  color: #111827;
}

.receipt-table tfoot td {
  background: #f9fafb;
  font-weight: 700;
}

.daily-summary {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.summary-table {
  width: 45%;
  border-collapse: collapse;
  font-size: 11px;
}

.summary-table th,
.summary-table td {
  border: 1px solid #e5e7eb;
  padding: 4px 6px;
  text-align: center;
}

.summary-table th {
  background: #f3f4f6;
  font-weight: 700;
}

.footer-note {
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
}

.signature {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sig-line {
  width: 200px;
  height: 1px;
  background: #9ca3af;
  margin-bottom: 4px;
}

.sig-label {
  font-size: 12px;
  color: #4b5563;
  font-weight: 600;
}
</style>
